import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from "react";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useDispatch, useSelector } from "react-redux";
import Swal from 'sweetalert2';
import { addUpdateCms, getCmsPageDetails } from '../../slice/cmsSlice';

const modules = {
    toolbar: [
        [{ header: [1, 2, 3, 4, 5] }, { font: [] }],
        [{ size: [] }],
        ['bold', 'underline', 'italic', 'strike'],
        [{ color: [] }, { background: [] }],
        ['code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ['link'],
    ],
}

const TextEditor = ({ tag_name }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [contentEn, setContentEn] = useState('')
    const [contentAr, setContentAr] = useState('')
    const [activeTab, setActiveTab] = useState('en')

    // Use cmsDetail (single record by tag_name), not cmsList
    const { cmsDetail, loading, error } = useSelector((state) => state.cms)

    useEffect(() => {
        if (tag_name) {
            dispatch(getCmsPageDetails({ tag_name }))
        }
    }, [dispatch, tag_name])

    // Populate editors once detail loads
    useEffect(() => {
        if (cmsDetail && cmsDetail.tag_name === tag_name) {
            setContentEn(cmsDetail.content || '')
            setContentAr(cmsDetail.content_ar || '')
        }
    }, [cmsDetail, tag_name])

    const handleSubmit = () => {
        const payload = {
            tag_name,
            title: tag_name,
            content: contentEn,
            title_ar: tag_name,
            content_ar: contentAr,
        }

        dispatch(addUpdateCms(payload)).then((response) => {
            if (response?.payload?.code === 200) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: 'Saved successfully',
                    showConfirmButton: false,
                    timer: 1500,
                })
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Something went wrong. Please try again.',
                    confirmButtonText: 'OK',
                })
            }
        })
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    // Only show error block for actual errors, not no_data_found (which is expected on first load)
    if (error) {
        return (
            <div className="alert alert-danger m-4">Error: {error}</div>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0" style={{ textTransform: 'capitalize' }}>
                    {tag_name?.replace(/_/g, ' ') || 'CMS Page'}
                </h4>
            </div>

            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4">

                    {/* Language Tabs */}
                    <ul className="nav nav-tabs mb-4" style={{ borderBottom: '2px solid #dee2e6' }}>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'en' ? 'active fw-semibold' : 'text-muted'}`}
                                onClick={() => setActiveTab('en')}
                                style={{
                                    border: 'none',
                                    borderBottom: activeTab === 'en' ? '2px solid #0d6efd' : '2px solid transparent',
                                    borderRadius: 0,
                                    marginBottom: '-2px',
                                    fontSize: '14px',
                                    padding: '8px 20px',
                                    background: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                🇬🇧 English
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'ar' ? 'active fw-semibold' : 'text-muted'}`}
                                onClick={() => setActiveTab('ar')}
                                style={{
                                    border: 'none',
                                    borderBottom: activeTab === 'ar' ? '2px solid #0d6efd' : '2px solid transparent',
                                    borderRadius: 0,
                                    marginBottom: '-2px',
                                    fontSize: '14px',
                                    padding: '8px 20px',
                                    background: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                🇦🇪 Arabic
                            </button>
                        </li>
                    </ul>

                    {/* English Editor */}
                    {activeTab === 'en' && (
                        <div>
                            <label className="form-label fw-semibold mb-2">
                                Content (English) <span style={{ color: 'red' }}>*</span>
                            </label>
                            <div style={{ marginBottom: '60px' }}>
                                <ReactQuill
                                    theme="snow"
                                    value={contentEn}
                                    onChange={setContentEn}
                                    modules={modules}
                                    style={{ height: '400px' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Arabic Editor */}
                    {activeTab === 'ar' && (
                        <div>
                            <label className="form-label fw-semibold mb-2">
                                Content (Arabic) <span style={{ color: 'red' }}>*</span>
                            </label>
                            <div style={{ marginBottom: '60px' }}>
                                <ReactQuill
                                    theme="snow"
                                    value={contentAr}
                                    onChange={setContentAr}
                                    modules={modules}
                                    style={{ height: '400px', direction: 'rtl' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/dashboard')}
                            style={{ padding: '8px 24px', fontSize: '14px', borderRadius: '6px' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleSubmit}
                            style={{ padding: '8px 24px', fontSize: '14px', borderRadius: '6px' }}
                        >
                            Save Changes
                        </button>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default TextEditor